module.exports = function buildModelingSlippageFeesAndMarketImpact({ agentProofPoints }) {
  const SOURCE_VERIFIED_DATE = '2026-08-14';

  return {
    path: '/guides/modeling-slippage-fees-and-market-impact',
    title: 'Model Slippage, Fees, and Market Impact | BotSpot',
    description:
      'Learn how to model commissions, bid-ask spreads, slippage, fill timing, liquidity, partial fills, and market impact without inventing universal assumptions.',
    pageType: 'article',
    lastmod: SOURCE_VERIFIED_DATE,
    content: {
      eyebrow: 'Backtesting execution-cost guide',
      heading:
        'Model slippage, fees, and market impact without making a backtest look safer than it is',
      summary:
        'Separate explicit fees from spread and execution effects, define when and how orders fill, scale assumptions to liquidity and order size, then test whether strategy conclusions survive less favorable execution.',
      primaryAction: {
        label: 'Backtest a strategy in BotSpot',
        url: '/ai-agent',
      },
      secondaryAction: {
        label: 'Read the backtesting guide',
        url: '/guides/backtesting-ai-trading-strategies',
      },
      proofPoints: agentProofPoints,
      sections: [
        {
          heading: '1. Build an execution-cost ledger before running the backtest',
          paragraphs: [
            'List every cost and fill assumption separately before inspecting performance. A single generic transaction-cost number can hide double counting, missing costs, or unrealistic execution.',
            'Keep explicit charges, spread, price movement, and market impact identifiable even if the backtesting engine combines some of them into one fill price.',
          ],
          bullets: [
            'Broker commissions, minimum charges, and volume tiers.',
            'Exchange, clearing, regulatory, contract, and asset-specific fees where applicable.',
            'Bid-ask spread paid when an order takes available liquidity.',
            'Price movement between the strategy decision, order submission, and fill.',
            'Liquidity limits, partial fills, queue behavior, and market impact.',
          ],
        },
        {
          heading: '2. Model commissions from the intended broker workflow',
          paragraphs: [
            'Use the current official fee schedule for the intended broker, account type, market, asset, order, and volume tier. Do not copy a fee from a different broker or assume commission-free means cost-free.',
            'Represent each fee in the form it is charged: per order, per share, per contract, percentage of value, minimum charge, or tiered rate. LumiBot supports flat, percentage, and per-contract trading-fee inputs for backtests.',
          ],
          bullets: [
            'Record source URL and verification date.',
            'Apply buy-side and sell-side charges where required.',
            'Include minimums and tier thresholds instead of averaging them away.',
            'Recheck volatile fee schedules before publishing or rerunning material analysis.',
          ],
        },
        {
          heading: '3. Keep spread and slippage definitions consistent',
          paragraphs: [
            'A marketable buy generally executes against the ask and a marketable sell against the bid. A fill model using bid and ask quotes can therefore include spread directly in simulated fill prices.',
            'Slippage describes a difference between a declared reference price and the simulated or actual fill. State whether the reference is decision price, quote midpoint, arrival price, last trade, bar close, or another observable value.',
          ],
          bullets: [
            'Do not subtract a separate spread charge when the fill model already buys at ask and sells at bid.',
            'Do not treat a bar close as both decision price and guaranteed fill unless timing makes that possible.',
            'Use quote data when available; document any bar-based spread proxy when it is not.',
            'Keep favorable price improvement possible only when the selected model and evidence support it.',
          ],
        },
        {
          heading: '4. Make fill timing and order behavior feasible',
          paragraphs: [
            'Define when the strategy observes data, when it submits an order, which market session applies, and which later observation can first produce a fill. Same-bar fills can use information unavailable when the order would have been placed.',
            'Order type changes the simulation. Market orders favor execution certainty but can slip. Limit orders constrain price but may remain unfilled. Stops become eligible only after their trigger conditions occur.',
          ],
          bullets: [
            'Match data resolution to decision and execution timing.',
            'Reject fills based on stale prices or closed-market data.',
            'Model unfilled, canceled, expired, and rejected orders where relevant.',
            'Preserve pending orders across bars instead of silently converting them into fills.',
          ],
        },
        {
          heading: '5. Treat liquidity and partial fills as strategy inputs',
          paragraphs: [
            'Historical price bars do not guarantee that the full strategy order could trade at one displayed price. Available quantity, order-book depth, queue position, and competing orders affect both fill quantity and timing.',
            'Set liquidity rules from data and intended trade size rather than one universal volume percentage. Test what happens when only part of an order fills and the remainder stays open, reprices, expires, or is canceled.',
          ],
          bullets: [
            'Compare order size with relevant traded volume and available depth.',
            'Prevent a simulated fill from consuming unlimited quantity at one price.',
            'Update cash, positions, exits, and risk rules after each partial fill.',
            'Check whether delayed completion changes later signals or creates overlapping orders.',
          ],
        },
        {
          heading: '6. Scale market impact to size, liquidity, volatility, and time',
          paragraphs: [
            'Market impact is the price response associated with consuming liquidity and revealing demand or supply. Its size depends on factors including order size, trading volume, depth, volatility, and execution duration.',
            'A bar-only backtest cannot observe the order book the hypothetical trade would have consumed. Any impact result remains a model estimate. Declare model form, inputs, calibration period, and unsupported markets.',
          ],
          bullets: [
            'Avoid one fixed market-impact value across symbols and order sizes.',
            'Increase scrutiny when orders represent a larger share of available liquidity.',
            'Distinguish temporary execution effects from persistent price movement when the model does.',
            'Recalibrate external model defaults before relying on them in a different market or period.',
          ],
        },
        {
          heading: '7. Run sensitivity tests instead of choosing one convenient cost',
          paragraphs: [
            'Test a documented base case plus less favorable execution cases. Derive ranges from current fee schedules, historical quotes, intended participation, and observed execution rather than publishing universal defaults.',
            'Report gross results, net results, turnover, total modeled costs, and the cost level at which the strategy conclusion changes. A strategy surviving only its most optimistic fill model needs more evidence.',
          ],
          bullets: [
            'Widen spreads using observed distributions for relevant symbols and sessions.',
            'Delay fills and reduce eligible fill quantity.',
            'Raise explicit costs according to plausible account or volume tiers.',
            'Stress high-volatility, low-liquidity, and extended-hours periods separately.',
            'Change one assumption at a time, then test combined adverse conditions.',
          ],
        },
        {
          heading: '8. Compare models with observed execution carefully',
          paragraphs: [
            'Paper trading can validate order construction, scheduling, state, and broker integration, but its fills remain simulated. Alpaca documents that its paper environment omits market impact, latency slippage, queue position, price improvement, and regulatory fees.',
            'Where controlled live observations exist, compare decision timestamp, reference quote, submit time, acknowledgments, partial fills, final fill, explicit fees, order size, and market conditions. Historical observations can inform calibration but cannot guarantee future execution.',
          ],
        },
        {
          heading: 'Limitations and risk',
          paragraphs: [
            'No execution model reproduces every venue, queue, counterparty, outage, rejection, or future liquidity condition. More detailed assumptions reduce known simplifications; they do not turn simulated results into expected returns.',
            'Broker fees, market structure, data coverage, and backtesting-engine defaults can change. Preserve configuration, sources, dates, and raw trade records so results can be audited and rerun.',
          ],
        },
      ],
      faq: [
        {
          question: 'What trading costs should a backtest include?',
          answer:
            'Include applicable broker, exchange, clearing, regulatory, and contract fees plus bid-ask spread, slippage, fill timing, liquidity limits, partial fills, and market impact. Financing, borrow, conversion, and product costs may also matter for some strategies.',
        },
        {
          question: 'What slippage value should I use?',
          answer:
            'No universal value fits every strategy. Derive assumptions from intended order type, asset, session, quote data, volatility, liquidity, order size, latency, and observed execution. Test multiple documented cases.',
        },
        {
          question: 'Does commission-free trading remove transaction costs?',
          answer:
            'No. Spread, price movement, market impact, regulatory or product fees, financing, and missed or partial fills can remain even when the broker charges no stated commission.',
        },
        {
          question: 'Can paper trading estimate live slippage accurately?',
          answer:
            'Not by itself. Paper systems use simulated fill and liquidity rules and may omit market impact, queue position, latency effects, fees, or price improvement. Use paper trading mainly for operational validation and document its fill assumptions.',
        },
        {
          question: 'How should a backtest handle partial fills?',
          answer:
            'Fill only eligible quantity under the declared liquidity model, update portfolio state after each fill, and keep the remainder pending, canceled, or expired according to the order rules. Do not assume every order completes immediately.',
        },
      ],
      relatedLinks: [
        {
          label: 'Backtest AI trading strategies',
          url: '/guides/backtesting-ai-trading-strategies',
        },
        {
          label: 'Understand backtesting metrics',
          url: '/guides/backtesting-metrics-explained',
        },
        {
          label: 'Compare paper and live trading',
          url: '/guides/paper-trading-vs-live-trading',
        },
        {
          label: 'Avoid overfitting trading strategies',
          url: '/guides/avoid-overfitting-trading-strategies',
        },
      ],
      sources: [
        {
          label: 'LumiBot trading-fee configuration',
          url: 'https://lumibot.lumiwealth.com/getting_started.html',
        },
        {
          label: 'QuantConnect trade fill models',
          url: 'https://www.quantconnect.com/docs/v2/writing-algorithms/reality-modeling/trade-fills/key-concepts',
        },
        {
          label: 'QuantConnect slippage models',
          url: 'https://www.quantconnect.com/docs/v2/writing-algorithms/reality-modeling/slippage/supported-models',
        },
        {
          label: 'QuantConnect transaction-fee models',
          url: 'https://www.quantconnect.com/docs/v2/writing-algorithms/reality-modeling/transaction-fees/supported-models',
        },
        {
          label: 'Alpaca paper trading assumptions',
          url: 'https://docs.alpaca.markets/us/v1.4.2/docs/paper-trading',
        },
        {
          label: 'Alpaca order behavior',
          url: 'https://docs.alpaca.markets/us/docs/orders-at-alpaca',
        },
        {
          label: 'Investor.gov fee bulletin',
          url: 'https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins/updated',
        },
        {
          label: 'Investor.gov bid-ask spread explanation',
          url: 'https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins-24',
        },
      ],
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  };
};
